/* eslint-disable @woocommerce/dependency-group */
/* global jQuery,finix_apple_pay_params */
import { useState, useEffect, useRef, createElement } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { registerPaymentMethod } from '@woocommerce/blocks-registry';
import { decodeEntities } from '@wordpress/html-entities';
import { getSetting } from '@woocommerce/settings';
import { cartStore } from '@woocommerce/block-data';
import { useSelect } from '@wordpress/data';

const gatewaySlug = 'finix_apple_pay_gateway';

const gatewaySettings = getSetting( 'finix_apple_pay_gateway_data', {} );

const label = decodeEntities( gatewaySettings.title ) || __( 'Pay with Apple Pay', 'finix-for-woocommerce' );

const nonEmptyValue = ( value ) => value && value.trim() !== '';

/*
 * @see https://developer.apple.com/documentation/applepayontheweb/apple-pay-on-the-web-version-history
 */
const applePayWebVersion = 10;

/**
 * Retrieve shipping data from the checkout form.
 * We use billing data as default as shipping may actually be hidden.
 */
const retrieveShippingData = () => {
	const billing = finix_apple_pay_params.billing_data;
	const shipping = finix_apple_pay_params.shipping_data;

	// Collect shipping address data.
	return {
		shipping_country: shipping.country || billing.country || '',
		shipping_state: shipping.state || billing.state || '',
		shipping_postcode: shipping.postcode || billing.postcode || '',
		shipping_city: shipping.city || billing.city || '',
		shipping_address_1: shipping.address_1 || billing.address1 || '',
	};
};

/**
 * Validate shipping as a Promise.
 */
const validateShippingData = () => {
	return new Promise( ( resolve ) => {
		let shippingData = retrieveShippingData();
		shippingData.nonce = finix_apple_pay_params.nonce;

		fetch( finix_apple_pay_params.url.ajax + '?action=validate_shipping_method', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify( shippingData ),
		} )
			.then( ( response ) => response.json() )
			.then( ( response ) => {
				if ( response.success === true ) {
					resolve( true );
				} else {
					resolve( false );
				}
			} )
			.catch( () => resolve( false ) );
	} );
};

/**
 * Manage the Apple Pay payment.
 */
const beginFinixApplePaySession = async ( applePaySession ) => {
	return new Promise( ( resolve, reject ) => {
		/*
		 * @see https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778021-onvalidatemerchant
		 */
		applePaySession.onvalidatemerchant = async ( event ) => {
			try {
				if ( ! nonEmptyValue( event.validationURL ) ) {
					reject( finix_apple_pay_params.text.error_processing );
					return;
				}

				const merchantRequest = {
					provider: 'APPLE_PAY',
					validation_url: event.validationURL,
					merchant_identity: finix_apple_pay_params.merchant_identity,
					domain_name: window.location.hostname,
					display_name: finix_apple_pay_params.merchant_name,
					session_request: true,
					wp_nonce: finix_apple_pay_params.nonce,
				};

				const merchantResponse = await fetch( finix_apple_pay_params.url.webhook, {
					method: 'POST',
					body: JSON.stringify( merchantRequest ),
					headers: {
						'Content-Type': 'application/json',
					},
				} );

				const merchantData = await merchantResponse.json();
				const sessionDetails = merchantData?.response?.session_details || '';
				if ( ! sessionDetails ) {
					reject( finix_apple_pay_params.text.error_processing );
					return;
				}

				const merchantSession = JSON.parse( sessionDetails );

				/*
				 * @see https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778015-completemerchantvalidation
				 */
				applePaySession.completeMerchantValidation( merchantSession );
			} catch ( error ) {
				reject( finix_apple_pay_params.text.error_processing );
			}
		};

		/*
		 * @see https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778020-onpaymentauthorized/
		 */
		applePaySession.onpaymentauthorized = async ( event ) => {
			try {
				// Token received from Apple Pay.
				const paymentToken = event.payment;

				if ( ! paymentToken ) {
					applePaySession.completePayment( window.ApplePaySession.STATUS_FAILURE );
					reject( finix_apple_pay_params.text.error_processing );
					return;
				}

				const tokenRequest = {
					provider: 'APPLE_PAY',
					payment_token: JSON.stringify( paymentToken ),
					process_payment: true,
					merchant_identity: finix_apple_pay_params.merchant_identity,
					wp_nonce: finix_apple_pay_params.nonce,
					billing_info: finix_apple_pay_params.billing_data,
				};

				const tokenResponse = await fetch( finix_apple_pay_params.url.webhook, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify( tokenRequest ),
				} );

				const tokenData = await tokenResponse.json();

				if ( tokenData.status === 201 ) {
					applePaySession.completePayment( window.ApplePaySession.STATUS_SUCCESS );
					document.getElementById( 'finix_apple_pay_success' ).value = true;
					document.getElementById( 'finix_apple_pay_transaction_id' ).value = tokenData.response.id;
					resolve( true );
				} else {
					applePaySession.completePayment( window.ApplePaySession.STATUS_FAILURE );
					document.getElementById( 'finix_apple_pay_success' ).value = false;
					document.getElementById( 'finix_apple_pay_transaction_id' ).value = '';
					reject( tokenData.data.message );
				}
			} catch ( err ) {
				applePaySession.completePayment( window.ApplePaySession.STATUS_FAILURE );
				document.getElementById( 'finix_apple_pay_success' ).value = false;
				reject( finix_apple_pay_params.text.error_processing );
			}
		};

		/*
		 * @see https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778029-oncancel/
		 */
		applePaySession.oncancel = async function () {
			reject();
		};

		/*
		 * @see https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778001-begin
		 */
		applePaySession.begin();
	} );
};

/**
 * Label component.
 *
 * @param {*} props Props from payment API.
 */
const Label = ( props ) => {
	const { PaymentMethodLabel } = props.components;

	const Icon = () => {
		return createElement( 'img', {
			src: gatewaySettings.icon,
			style: {
				// TODO: move to a separate CSS style file.
				position: 'absolute',
				right: '20px',
				height: '30px',
				width: 'auto',
			},
		} );
	};

	return <PaymentMethodLabel text={ label } icon={ <Icon /> } />;
};

const Content = ( props ) => {
	const { eventRegistration, emitResponse } = props;
	const { onPaymentSetup } = eventRegistration;
	const { shippingAddress, billingAddress, totals, coupons, items } = useSelect( ( select ) => select( cartStore ).getCartData(), [] );
	const [ isValidShipping, setIsValidShipping ] = useState( true );
	const lastShipKeyRef = useRef( null );
	const initializedRef = useRef( false );

	useEffect( () => {
		finix_apple_pay_params.shipping_data = shippingAddress;

		// Serialize to compare by value (Blocks gives you new object refs).
		const key = JSON.stringify( shippingAddress || {} );

		// If we already validated this exact address skip.
		if ( initializedRef.current && key === lastShipKeyRef.current ) return;

		lastShipKeyRef.current = key;
		initializedRef.current = true;
		( async () => {
			const isValid = await validateShippingData();
			setIsValidShipping( isValid );
		} )();
	}, [ shippingAddress ] );

	useEffect( () => {
		finix_apple_pay_params.billing_data = billingAddress;
	}, [ billingAddress ] );

	// When the totals change, update the amount.
	useEffect( () => {
		finix_apple_pay_params.amount = ( totals.total_price / 100 ).toFixed( 2 );
		finix_apple_pay_params.currency_code = totals.currency_code;
		finix_apple_pay_params.subtotal = ( totals.total_items / 100 ).toFixed( 2 );
		finix_apple_pay_params.shipping_amount = ( totals.total_shipping / 100 ).toFixed( 2 );
		finix_apple_pay_params.tax_amount = ( totals.total_tax / 100 ).toFixed( 2 );
	}, [ totals ] );

	// When the coupons change, update the applied coupons.
	useEffect( () => {
		finix_apple_pay_params.coupons = coupons.map( ( coupon ) => ( {
			code: coupon.code,
			total: ( coupon.totals.total_discount / 100 ).toFixed( 2 ),
		} ) );
	}, [ coupons ] );

	// When the items change, update the applied items.
	useEffect( () => {
		finix_apple_pay_params.items = items.map( ( item ) => ( {
			name: item.name,
			quantity: item.quantity,
			image: item.images.length && item.images.length > 0 ? item.images[ 0 ].thumbnail : '',
			total: item.prices.price * item.quantity,
		} ) );
	}, [ items ] );

	// Triggered on our payment method selection.
	useEffect( () => {
		// Triggered on "Place Order" button click only.
		const unsubscribe = onPaymentSetup( async () => {
			if ( ! isValidShipping ) {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: __( 'Please make sure shipping and billing information is correct.', 'finix-for-woocommerce' ),
				};
			}

			let lineItems = [];

			// Add product line items if value is not zero.
			Object.values( finix_apple_pay_params.products ).forEach( ( product ) => {
				if ( parseFloat( product.total ) !== 0 ) {
					lineItems.push( {
						label: product.quantity > 1 ? `${ product.name } x${ product.quantity }` : product.name,
						type: 'final',
						amount: product.total,
					} );
				}
			} );

			lineItems.push( {
				label: __( 'Subtotal', 'finix-for-woocommerce' ),
				type: 'final',
				amount: finix_apple_pay_params.subtotal || finix_apple_pay_params.amount,
			} );

			// Add any offer details to the transaction info.
			lineItems.push(
				...finix_apple_pay_params.coupons.map( ( offer ) => ( {
					label: __( 'Discount code:', 'finix-for-woocommerce' ) + ' ' + offer.code,
					type: 'final',
					amount: -1 * parseFloat( offer.total ).toFixed( 2 ), // Ensure negative value for discounts.
				} ) )
			);

			// Add Shipping if is not Zero.
			if ( finix_apple_pay_params.shipping_amount > 0 ) {
				lineItems.push( {
					label: __( 'Shipping', 'finix-for-woocommerce' ),
					type: 'final',
					amount: finix_apple_pay_params.shipping_amount || '0.00',
				} );
			}

			// Add Tax if is not Zero.
			if ( finix_apple_pay_params.tax_amount > 0 ) {
				lineItems.push( {
					label: __( 'Tax', 'finix-for-woocommerce' ),
					type: 'final',
					amount: finix_apple_pay_params.tax_amount || '0.00',
				} );
			}

			const applePaySession = new window.ApplePaySession( applePayWebVersion, {
				countryCode: finix_apple_pay_params.merchant_country,
				currencyCode: finix_apple_pay_params.currency_code,
				merchantCapabilities: [ 'supports3DS' ],
				supportedNetworks: [ 'visa', 'masterCard', 'amex', 'discover' ],
				total: {
					label: finix_apple_pay_params.merchant_name,
					amount: finix_apple_pay_params.amount,
				},
				lineItems: lineItems,
			} );

			try {
				const isPaymentSuccessful = await beginFinixApplePaySession( applePaySession );

				if ( isPaymentSuccessful === true ) {
					return {
						type: emitResponse.responseTypes.SUCCESS,
						meta: {
							paymentMethodData: {
								finix_apple_pay_success: document.getElementById( 'finix_apple_pay_success' ).value,
								finix_apple_pay_transaction_id: document.getElementById( 'finix_apple_pay_transaction_id' ).value,
								finix_nonce: finix_apple_pay_params.nonce,
							},
						},
					};
				}
			} catch ( error ) {
				return {
					type: emitResponse.responseTypes.FAIL,
					message: error?.toString(),
				};
			}

			return {
				type: emitResponse.responseTypes.FAIL,
				message: finix_apple_pay_params.text.error_processing,
			};
		} );

		// Clean up the event listener when the component unmounts.
		return unsubscribe;
	}, [ onPaymentSetup ] );

	return createElement(
		'fieldset',
		{
			id: 'wc-' + gatewaySlug + '-form',

			style: { background: 'transparent', padding: '0' },
		},
		// If gatewaySettings.description is not empty, create div > p to display.
		nonEmptyValue( gatewaySettings.description )
			? createElement(
					'div',
					{},
					createElement( 'p', {
						dangerouslySetInnerHTML: {
							__html: gatewaySettings.description,
						},
					} )
			  )
			: null,
		// Create hidden inputs for Apple Pay status and transaction id.
		createElement( 'apple-pay-button', {
			id: 'finix-apple-pay-button',
			buttonstyle: finix_apple_pay_params.button_style,
			type: finix_apple_pay_params.button_type,
			locale: finix_apple_pay_params.button_locale,
		} ),
		createElement( 'input', {
			type: 'hidden',
			name: 'finix_apple_pay_success',
			id: 'finix_apple_pay_success',
		} ),
		createElement( 'input', {
			type: 'hidden',
			name: 'finix_apple_pay_transaction_id',
			id: 'finix_apple_pay_transaction_id',
		} )
	);
};

const Edit = () => {};

/**
 * Finix payment method config object.
 *
 * @see https://developer.woocommerce.com/docs/cart-and-checkout-payment-method-integration-for-the-checkout-block/#registration
 */
registerPaymentMethod( {
	name: gatewaySlug,
	label: <Label />,
	content: <Content />,
	edit: <Edit />,
	canMakePayment: () => window.ApplePaySession && window.ApplePaySession.canMakePayments() && window.ApplePaySession.supportsVersion( applePayWebVersion ),
	ariaLabel: label,
	supports: {
		features: gatewaySettings.supports,
	},
} );
